import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream">
        <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
          <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
            <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">Reset Password</h1>
            <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">Invalid reset link. Please request a new one.</p>
          </div>
        </section>
        <div className="flex items-center justify-center px-4 py-6 sm:py-12">
          <Link to="/forgot-password" className="text-teal-600 hover:underline text-sm">Request Password Reset</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: form.password }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(data.detail || 'Reset failed');
      }
    } catch {
      toast.error('Something went wrong');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="reset-password-success">
        <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
          <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
            <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">Password Reset</h1>
            <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">Your password has been updated successfully.</p>
          </div>
        </section>
        <div className="flex items-center justify-center px-4 py-6 sm:py-12">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-card p-6 sm:p-8 text-center">
            <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-xl font-display font-semibold mb-2">All Done</h2>
            <p className="text-sm text-gray-500 mb-6">You can now sign in with your new password.</p>
            <Button onClick={() => navigate('/login')} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" data-testid="go-to-login-btn">
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="reset-password-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">Reset Password</h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">Enter your new password below.</p>
        </div>
      </section>

      <div className="flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-card p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">New Password</Label>
              <Input
                type="password" required placeholder="Min 6 characters"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 h-11" data-testid="reset-password-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Confirm Password</Label>
              <Input
                type="password" required placeholder="Confirm your password"
                value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                className="mt-1 h-11" data-testid="reset-confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-full" disabled={loading} data-testid="reset-submit-btn">
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
}
